/**
 * Type definitions for Announcements feature
 *
 * Defines types for announcement configuration, public display data,
 * and related data structures.
 */

/**
 * Announcement
 *
 * Represents a single announcement with date range and message.
 */
export interface Announcement {
  id: string;
  studio: string;
  studio_name: string;
  title: string;
  message: string;
  start_month: number;
  start_day: number;
  end_month: number;
  end_day: number;
  repeat_yearly: boolean;
  is_active: boolean;
  /**
   * Manual sort position (lower = first). Server-controlled; the only way
   * to change it is via the dedicated reorder endpoint hit by the drag UI.
   */
  display_order: number;
  date_range_display: string;
  is_cross_year: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Announcement Form Data
 *
 * Data structure for creating or updating an announcement.
 */
export interface AnnouncementFormData {
  title?: string;
  message: string;
  start_month: number;
  start_day: number;
  end_month: number;
  end_day: number;
  repeat_yearly?: boolean;
  is_active?: boolean;
}

/**
 * Announcement Configuration
 *
 * Controls how announcements are displayed on the public widget page.
 */
export interface AnnouncementConfig {
  id: string;
  studio: string;
  studio_name: string;
  greeting_message: string;
  external_url: string;
  settings: Record<string, unknown>;
  embed_code: string;
  created_at: string;
  updated_at: string;
}

/**
 * Announcement Configuration Update Payload
 */
export interface AnnouncementConfigUpdate {
  greeting_message?: string;
  external_url?: string;
  settings?: Record<string, unknown>;
}

/**
 * Public announcement data for display
 */
export interface PublicAnnouncementItem {
  title: string;
  message: string;
  date_range_display: string;
  is_cross_year: boolean;
}

/**
 * Studio information for public display
 */
export interface PublicStudioInfo {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  owner_plan_slug?: string | null;
}

/**
 * Configuration data for public display
 */
export interface PublicAnnouncementConfig {
  greeting_message: string;
  external_url: string;
}

/**
 * Theme data for public display
 */
export interface PublicThemeData {
  tokens: {
    colors: Record<string, unknown>;
    typography: Record<string, unknown>;
    shape: Record<string, unknown>;
    modes: Record<string, unknown>;
  };
  generated: Record<string, unknown>;
}

/**
 * Complete public announcement widget response
 */
export interface PublicAnnouncementWidget {
  config: PublicAnnouncementConfig;
  studio: PublicStudioInfo;
  announcements: PublicAnnouncementItem[];
  theme: PublicThemeData | null;
}

/**
 * Month option for UI selection
 */
export interface MonthOption {
  value: number;
  label: string;
}

/**
 * Constants for month options
 */
export const MONTH_OPTIONS: MonthOption[] = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

/**
 * Helper function to get days in month
 */
export const getDaysInMonth = (month: number): number => {
  const daysInMonth: Record<number, number> = {
    1: 31,
    2: 29, // Allow Feb 29 for leap years
    3: 31,
    4: 30,
    5: 31,
    6: 30,
    7: 31,
    8: 31,
    9: 30,
    10: 31,
    11: 30,
    12: 31,
  };
  return daysInMonth[month] || 31;
};

/**
 * Helper function to generate day options for a given month
 */
export const getDayOptions = (month: number): number[] => {
  const maxDays = getDaysInMonth(month);
  return Array.from({ length: maxDays }, (_, i) => i + 1);
};
